import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Trash2 } from "react-feather";
import toast from "react-hot-toast";
import { useGetStockOutQuery, useDeleteStockOutMutation } from "../../stockApi";
import { useGetCustomerQuery } from "../../../masters/customers/customersApi";
import { useCanPrintStockOut, useCanDeleteStockOut } from "../../useStockPermissions";
import ReasonPrompt from "../../components/ReasonPrompt";
import StockDocumentPrintModal from "../../components/print/StockDocumentPrintModal";
import { paths } from "../../../../routes/paths";

const StockOutDetail = () => {
  const { recordGuid } = useParams();
  const canPrint = useCanPrintStockOut();
  const canDelete = useCanDeleteStockOut();
  const { data: stockOut, isLoading, isError, error } = useGetStockOutQuery(recordGuid);
  const [deleteStockOut] = useDeleteStockOutMutation();
  const [showPrint, setShowPrint] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);

  // Unlike LPO's free-text supplier, Stock-Out's customer recipient has a
  // real IssuedToCustomerId+IssuedToCustomerBranchId pair, so Address/Mobile
  // can always be reliably looked up for print, no caveat.
  const { data: recipientCustomer } = useGetCustomerQuery(
    { branchId: stockOut?.issuedToCustomerBranchId, custId: stockOut?.issuedToCustomerId },
    { skip: !(stockOut?.issuedToType === "CUSTOMER" && stockOut?.issuedToCustomerId) }
  );

  const handleDelete = async (deleteReason) => {
    try {
      await deleteStockOut({ recordGuid, deleteReason: deleteReason || null }).unwrap();
      toast.success("Stock-Out deleted - stock balance reversed");
      setShowDeleteForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not delete the Stock-Out");
    }
  };

  if (isLoading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  if (isError || !stockOut) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.stockOut} className="d-inline-flex align-items-center mb-3"><ArrowLeft size={16} className="me-1" /> Back to Stock-Out</Link>
          <div className="alert alert-danger">Could not load this Stock-Out ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  const recipientName = stockOut.issuedToType === "EMPLOYEE" ? stockOut.issuedToEmployeeName : stockOut.issuedToCustomerName;

  const printProps = {
    title: "STOCK ISSUE NOTE",
    docNo: stockOut.stockOutNo,
    docDate: new Date(stockOut.stockOutDate).toLocaleDateString(),
    branchLabel: stockOut.branchCode,
    partyLabel: stockOut.issuedToType === "EMPLOYEE" ? "Issued To (Employee)" : "Issued To (Customer)",
    partyName: recipientName,
    partyAddress: recipientCustomer ? [recipientCustomer.address1, recipientCustomer.address2].filter(Boolean).join(", ") : "",
    partyMobile: recipientCustomer?.mobileNo || "",
    remarks: stockOut.issuedForPurpose ? `Purpose: ${stockOut.issuedForPurpose}${stockOut.remarks ? " - " + stockOut.remarks : ""}` : stockOut.remarks,
    showVat: false,
    // The rate on a Stock-Out line is filled from the last purchase price
    // (see LineItemsEditor's LastPurchasePriceButton), not a price charged
    // to the recipient - labeled accordingly on print to avoid confusion.
    priceColumnLabel: "Last Purchase Cost",
    lines: stockOut.lines.map((l) => ({ qty: l.issuedQty, productName: l.productName, price: l.unitRate, amount: l.lineAmount })),
    totals: { subTotal: stockOut.totalAmount, vatAmount: 0, netAmount: stockOut.totalAmount },
    preparedByName: stockOut.createdByName,
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockOut} className="d-inline-flex align-items-center mb-2"><ArrowLeft size={16} className="me-1" /> Back to Stock-Out</Link>
              <h4>{stockOut.stockOutNo}</h4>
              <h6>{stockOut.branchCode} - Issued to {stockOut.issuedToType === "EMPLOYEE" ? "Employee" : "Customer"}</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2">
            {canPrint && (
              <button type="button" className="btn btn-outline-primary d-inline-flex align-items-center gap-1" onClick={() => setShowPrint(true)}>
                <Printer size={16} /> Print
              </button>
            )}
            {canDelete && !stockOut.isDeleted && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowDeleteForm((s) => !s)}>
                <Trash2 size={16} /> Delete
              </button>
            )}
          </div>
        </div>

        {showDeleteForm && (
          <ReasonPrompt
            label="Delete Reason (optional) - this reverses the stock balance this Stock-Out removed"
            confirmLabel="Delete Stock-Out"
            required={false}
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteForm(false)}
          />
        )}

        {stockOut.isDeleted && (
          <div className="alert alert-danger">
            Deleted by {stockOut.deletedByName} on {new Date(stockOut.deletedDate).toLocaleString()}
            {stockOut.deleteReason ? `: ${stockOut.deleteReason}` : ""}
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-4"><strong>Issued To:</strong> {recipientName || "-"}</div>
              <div className="col-md-4"><strong>Purpose:</strong> {stockOut.issuedForPurpose || "-"}</div>
              <div className="col-md-4"><strong>Date:</strong> {new Date(stockOut.stockOutDate).toLocaleDateString()}</div>
              {stockOut.remarks && <div className="col-md-12 mt-2"><strong>Remarks:</strong> {stockOut.remarks}</div>}
            </div>

            <div className="table-responsive">
              <table className="table table-bordered">
                <thead><tr><th>#</th><th>Product</th><th>Issued Qty</th><th>Returned Qty</th><th>Unit</th><th>Rate</th><th>Amount</th></tr></thead>
                <tbody>
                  {stockOut.lines.map((l, idx) => (
                    <tr key={l.lineId}>
                      <td>{idx + 1}</td>
                      <td>{l.productName} ({l.productCode})</td>
                      <td>{l.issuedQty}</td>
                      <td>{l.returnedQty}</td>
                      <td>{l.unit}</td>
                      <td>{l.unitRate != null ? Number(l.unitRate).toFixed(2) : "-"}</td>
                      <td>{l.lineAmount != null ? Number(l.lineAmount).toFixed(2) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end mt-2">
              <div>Total Amount: <strong>{Number(stockOut.totalAmount).toFixed(2)}</strong></div>
            </div>

            <div className="text-muted small mt-3">
              Created by {stockOut.createdByName} on {new Date(stockOut.createdDate).toLocaleString()}
            </div>
          </div>
        </div>

        <StockDocumentPrintModal show={showPrint} onClose={() => setShowPrint(false)} docProps={printProps} copies={1} />
      </div>
    </div>
  );
};

export default StockOutDetail;
